import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowLeft, Gift } from "lucide-react";
import Btn from "../components/Btn";
import ThemeToggle from "../components/ThemeToggle";
import { useAuth } from "../context/AuthContext";
import { apiErr } from "../lib/api";

export default function Auth() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const [mode, setMode] = useState(refFromUrl ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", referral: refFromUrl });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav("/dashboard", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.email, form.password, form.name, form.referral);
      toast.success("Selamat datang di SkorUp! 🚀");
      nav("/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-su-bg flex flex-col">
      <div className="p-4 flex items-center justify-between">
        <Link to="/" data-testid="auth-back" className="flex items-center gap-2 font-heading font-extrabold text-su-muted hover:text-su-text">
          <ArrowLeft className="w-5 h-5" /> Beranda
        </Link>
        <ThemeToggle />
      </div>

      <div className="flex-1 grid place-items-center px-4 pb-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 justify-center mb-6">
            <div className="w-11 h-11 rounded-2xl bg-su-primary grid place-items-center text-white font-heading font-black text-xl shadow-[0_4px_0_var(--su-primary-shadow)]">S</div>
            <span className="font-heading font-black text-2xl text-su-text">SkorUp<span className="text-su-primary">.io</span></span>
          </div>

          <div className="card-chunky p-6 sm:p-8 animate-pop">
            <h1 className="font-heading font-black text-2xl text-su-text mb-1">
              {mode === "login" ? "Masuk ke Arena" : "Buat Akun Baru"}
            </h1>
            <p className="text-su-muted font-medium text-sm mb-6">
              {mode === "login" ? "Lanjutkan misi belajarmu." : "Mulai naikkan skormu hari ini."}
            </p>

            <button
              data-testid="google-login-btn"
              onClick={google}
              className="btn-tactile bg-su-surface text-su-text border-su-border w-full py-3 mb-4 flex items-center justify-center gap-3"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
              Lanjut dengan Google
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-0.5 bg-su-border" />
              <span className="text-xs font-bold text-su-muted uppercase">atau</span>
              <div className="flex-1 h-0.5 bg-su-border" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "register" && (
                <Field icon={User} testid="auth-name" placeholder="Nama lengkap" value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })} required />
              )}
              <Field icon={Mail} testid="auth-email" type="email" placeholder="Email" value={form.email}
                onChange={(v) => setForm({ ...form, email: v })} required />
              <Field icon={Lock} testid="auth-password" type="password" placeholder="Password" value={form.password}
                onChange={(v) => setForm({ ...form, password: v })} required />
              {mode === "login" && (
                <div className="text-right -mt-1">
                  <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-sm font-extrabold text-su-primary hover:underline">
                    Lupa password?
                  </Link>
                </div>
              )}
              {mode === "register" && (
                <Field icon={Gift} testid="auth-referral" placeholder="Kode referral (opsional)" value={form.referral}
                  onChange={(v) => setForm({ ...form, referral: v.toUpperCase() })} />
              )}
              <Btn data-testid="auth-submit" type="submit" disabled={busy} className="w-full mt-2">
                {busy ? "Memproses..." : mode === "login" ? "Masuk" : "Daftar Sekarang"}
              </Btn>
            </form>

            <p className="text-center text-sm text-su-muted font-medium mt-5">
              {mode === "login" ? "Belum punya akun? " : "Sudah punya akun? "}
              <button
                data-testid="auth-toggle-mode"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="text-su-primary font-extrabold hover:underline"
              >
                {mode === "login" ? "Daftar gratis" : "Masuk"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, type = "text", placeholder, value, onChange, required }) {
  return (
    <div className="relative">
      <Icon className="w-5 h-5 text-su-muted absolute left-4 top-1/2 -translate-y-1/2" />
      <input
        data-testid={testid}
        type={type}
        placeholder={placeholder}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-12 pr-4 py-3 rounded-2xl border-2 border-su-border bg-su-bg text-su-text font-medium outline-none focus:border-su-primary transition-colors"
      />
    </div>
  );
}
